import type {ReactElement} from 'react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Pill, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {VersionHistoryOnboardingPopup} from '@Components/version-history-onboarding-popup/version-history-onboarding-popup';
import {
  showVersionHistoryOnBoarding,
  markVersionHistoryOnboardingAsSeen,
  markVersionHistoryPulseAsSeen,
} from '@Components/poster-editor/library/poster-editor-library';
import {Text, TextSize} from '@Components/text';
import {Tooltip, TOOLTIP_POSITION} from '@Components/tooltip';
import styles from './auto-save-option.module.scss';
import {useAppSelector} from '@/hooks';
import {onClickOpenVersionHistoryPanel} from '@Components/poster-editor/library/poster-editor-library';
import {GA4EventName, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {isEditorMobileVariant} from "@Components/poster-editor/library/is-editor-mobile-variant";

interface AutoSaveOptionProps {
  showText: boolean;
  showLine: boolean;
  showVersionHistoryOnHover?: boolean;
}

export function AutoSaveOption({showText = true, showVersionHistoryOnHover = false, showLine = true}: AutoSaveOptionProps): ReactElement<any> {
  const isOnline = useAppSelector((state) => {
    return state.userOnlineStatus.isUserOnline;
  });
  const isPosterSaving = useAppSelector((state) => {
    return state.posterEditor.isPosterSaving;
  });
  const areImagesUploading = useAppSelector((state) => {
    return state.posterEditor.posterObject && state.posterEditor.posterObject.uploadingImages.length > 0;
  });
  const showTooltipForAutoSave = useAppSelector((state) => {
    return state.posterEditor.showAutoSaveTooltip;
  });
  const didPosterSaveFailed = useAppSelector((state) => {
    return state.posterEditor.didPosterSaveFailed;
  });
  const [showVersionHistoryOnboardingPopup, setShowVersionHistoryOnboardingPopup] = useState(false);
  const [isHoveringOnPill, setIsHoveringPill] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const doShowVersionHistoryOnboarding = async (): Promise<void> => {
    if (await showVersionHistoryOnBoarding()) {
      setShowVersionHistoryOnboardingPopup(true);
      void markVersionHistoryOnboardingAsSeen();
      markVersionHistoryPulseAsSeen();
    }
  };

  const shouldShowVersionHistoryOnHover = showVersionHistoryOnHover && isOnline;

  useEffect((): void => {
    void doShowVersionHistoryOnboarding();
  }, []);

  const getBackgroundColorForAutoSave = (): string => {
    if (!isOnline) {
      return styles.backgroundColorOffline;
    }
    if (didPosterSaveFailed) {
      return styles.backgroundColorFail;
    }

    return '';
  };

  const getIconColorForAutoSave = (): string => {
    if (!isOnline) {
      return styles.iconColorOffline;
    }
    if (didPosterSaveFailed) {
      return styles.iconColorError;
    }

    return styles.colorDefault;
  };

  const getTexColorForAutoSave = (): string => {
    if (isOnline && !didPosterSaveFailed) {
      return styles.colorDefault;
    }

    return 'content-body-white';
  };

  const getIconForAutoSave = (): string => {
    if (!isOnline) {
      return 'icon-offline';
    }
    if (didPosterSaveFailed) {
      return 'icon-offline';
    }
    if (isPosterSaving) {
      return 'icon-save-loading';
    }
    if (areImagesUploading) {
      return 'icon-save-loading';
    }
    return 'icon-auto-saved';
  };

  const getTextForAutoSave = (): string => {
    if (!showText) {
      return '';
    }

    if (!isOnline) {
      return window.i18next.t('pmwjs_offline');
    }

    if (didPosterSaveFailed) {
      return window.i18next.t('pmwjs_unable_to_save');
    }

    if (isPosterSaving) {
      return window.i18next.t('pmwjs_all_changes_saved_2');
    }

    if (areImagesUploading) {
      return window.i18next.t('pmwjs_saving_paused');
    }

    return window.i18next.t('pmwjs_all_changes_saved_2');
  };

  const getAutoSavePillClassName = (): string => {
    return `${!isOnline ? styles.offlinePill : shouldShowVersionHistoryOnHover ? styles.clickablePill : ''} ${styles.pillAutoSave} ${isHoveringOnPill && shouldShowVersionHistoryOnHover ? styles.pillHidden : ''}`;
  };

  const getVersionHistoryPillClassName = (): string => {
    return `${styles.clickablePill} ${styles.pillVersionHistory} ${isHoveringOnPill ? styles.pillVisible : styles.pillHidden}`;
  };

  const versionHistoryOnboardingPopupPosition = useMemo(() => {
    if (!containerRef.current) {
      return null;
    }
    const WIDTH_OF_POPUP = 320;
    const PADDING_FROM_PILL = 16;
    const rect = containerRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + PADDING_FROM_PILL,
      left: rect.left + rect.width * 0.5 - WIDTH_OF_POPUP * 0.5,
    };
  }, [showVersionHistoryOnboardingPopup]);

  const getVersionHistoryOnboardingPopup = (): ReactElement<any> | null => {
    if (!showVersionHistoryOnboardingPopup) {
      return null;
    }
    if (isEditorMobileVariant()) {
      return null;
    }
    if (!versionHistoryOnboardingPopupPosition) {
      return null;
    }

    return (
      <div
        style={{
          top: versionHistoryOnboardingPopupPosition.top,
          left: versionHistoryOnboardingPopupPosition.left,
        }}
        className={styles.onboardingPopup}
      >
        <VersionHistoryOnboardingPopup
          onClose={(): void => {
            setShowVersionHistoryOnboardingPopup(false);
            void markVersionHistoryOnboardingAsSeen();
          }}
        />
      </div>
    );
  };

  const getTooltipForOfflineMessage = (): ReactElement<any> | null => {
    if (showVersionHistoryOnboardingPopup) {
      return null;
    }

    if (!containerRef.current) {
      return null;
    }

    if (isOnline) {
      return null;
    }

    if (!isHoveringOnPill) {
      return null;
    }

    const PADDING_FROM_PILL = 12;
    const HALF_WIDTH_OF_TOOLTIP = 97.5;

    return (
      <Tooltip
        style={{
          position: 'fixed',
          top: containerRef.current.getBoundingClientRect().bottom + PADDING_FROM_PILL,
          left:
            containerRef.current.getBoundingClientRect().left + containerRef.current.getBoundingClientRect().width / 2 - HALF_WIDTH_OF_TOOLTIP - (isEditorMobileVariant() ? 4 : 0),
          zIndex: 5000,
        }}
        className={`${styles.tooltip} ${styles.offlineTooltip}`}
        position={TOOLTIP_POSITION.TOP_MIDDLE}
        id="autosave-tooltip"
        text={<Text val={window.i18next.t('pmwjs_autosave_offline_tooltip_text')} size={TextSize.XXSMALL} />}
      />
    );
  };

  const getTooltip = (): ReactElement<any> | null => {
    if (showVersionHistoryOnboardingPopup) {
      return null;
    }

    if (!showTooltipForAutoSave) {
      return null;
    }

    if (!containerRef.current) {
      return null;
    }

    const PADDING_FROM_PILL = 12;
    const HALF_WIDTH_OF_TOOLTIP = 70;

    return (
      <Tooltip
        style={{
          position: 'fixed',
          top: containerRef.current.getBoundingClientRect().bottom + PADDING_FROM_PILL,
          left: containerRef.current.getBoundingClientRect().left + containerRef.current.getBoundingClientRect().width / 2 - HALF_WIDTH_OF_TOOLTIP,
          zIndex: 5000,
        }}
        className={styles.tooltip}
        position={TOOLTIP_POSITION.TOP_MIDDLE}
        id="autosave-tooltip"
        text={<Text val={window.i18next.t('pmwjs_autosave_tooltip_text')} size={TextSize.XXSMALL} />}
      />
    );
  };

  const handlePillClick = (): void => {
    const isAllChangesSaved = isOnline && !didPosterSaveFailed && !isPosterSaving && !areImagesUploading;
    if (shouldShowVersionHistoryOnHover && isAllChangesSaved) {
      void markVersionHistoryOnboardingAsSeen();
      trackPosterBuilderGA4Events(GA4EventName.VERSION_HISTORY_OPEN, {
        [GA4EventParamName.SOURCE]: GA4EventSources.AUTO_SAVE_HOVER,
      });
      void onClickOpenVersionHistoryPanel();
    }
  };

  return (
    <>
      {showText && showLine ? <div className={`spacing-m-r-2 ${styles.autoSaveLine}`} /> : null}
      <div className={styles.container} ref={containerRef}>
        {showText ? (
          <>
            <Pill
              content={{
                type: PILL_CONTENT_TYPE.HYBRID,
                icon: getIconForAutoSave(),
                text: getTextForAutoSave(),
                isBold: false,
                textSize: TextSize.XXSMALL,
                textColor: getTexColorForAutoSave(),
                iconColor: getIconColorForAutoSave(),
                iconClasses: styles.iconClasses,
              }}
              size={PILL_SIZE.SMALL}
              background={getBackgroundColorForAutoSave()}
              onMouseEnter={(): void => {
                setIsHoveringPill(true);
              }}
              onMouseLeave={(): void => {
                setIsHoveringPill(false);
              }}
              onClick={handlePillClick}
              className={`spacing-m-r-2 spacing-p-r-2 spacing-p-l-2 _cursor-pointer ${getAutoSavePillClassName()}`}
            />
            {shouldShowVersionHistoryOnHover ? (
              <Pill
                content={{
                  type: PILL_CONTENT_TYPE.HYBRID,
                  icon: 'icon-history',
                  text: window.i18next.t('pmwjs_view_version_history'),
                  isBold: false,
                  textSize: TextSize.XXSMALL,
                  textColor: getTexColorForAutoSave(),
                  iconColor: getIconColorForAutoSave(),
                  iconClasses: styles.iconClasses,
                }}
                size={PILL_SIZE.SMALL}
                background={getBackgroundColorForAutoSave()}
                onMouseEnter={(): void => {
                  setIsHoveringPill(true);
                }}
                onMouseLeave={(): void => {
                  setIsHoveringPill(false);
                }}
                onClick={handlePillClick}
                className={`spacing-m-r-2 spacing-p-r-2 spacing-p-l-2 _cursor-pointer ${getVersionHistoryPillClassName()}`}
              />
            ) : null}
          </>
        ) : (
          <Icon
            onClick={(): void => {
              if (isEditorMobileVariant() && !isOnline) {
                setIsHoveringPill(true);
                setTimeout((): void => {
                  setIsHoveringPill(false);
                }, 3000);
              }
            }}
            icon={getIconForAutoSave()}
            size={IconSize.SIZE_ICON_20}
            shape={IconShape.SQUARE}
            type={IconType.DARK_GHOST}
            isDisabled={isOnline}
            className={`spacing-m-r-2 ${getIconColorForAutoSave()} ${getBackgroundColorForAutoSave()}`}
          />
        )}
        {getVersionHistoryOnboardingPopup()}
        {getTooltip()}
        {getTooltipForOfflineMessage()}
      </div>
    </>
  );
}
